import React, {useEffect, useState} from 'react';
import {css, styled} from 'styled-components';
import {colors} from '../../styles/colors';
import DefaultImageView from 'components/common/DefaultImageView';
import ScreenLayout from 'components/common/ScreenLayout';
import {getDevice} from 'utils/getDevice';
import DefaultText from 'components/common/DefaultText';
import {useRecoilValue} from 'recoil';
import DefaultButton from 'components/common/DefaultButton';
import {useLocation, useNavigate} from 'react-router-dom';
import ContainerHeader from 'components/common/ContainerHeader';
import {minmax} from 'utils/minmax';
import Footer from 'components/common/Footer';
import {
  restoreTongPlacePermission,
  searchUserPermission,
} from 'function/api';
import {userInfoState} from 'utils/Atoms';
import {
  IPermissionList,
  IWorkerTongPlacePermissionProfile,
  UserPermission,
} from 'interfaces/interface';
import {permissionStateStatus} from 'data/status';
import ChangeStateModal from 'components/userMenagement/ChangeStateModal';
import ResignationModal from 'components/userMenagement/ResignationModal';
import OwnerTransferModal from 'components/userMenagement/OwnerTransferModal';
import ChangeUserNameModal from 'components/userMenagement/ChangeUserNameModal';
import LabelInput from 'components/common/LabelInput';
import {formatPhoneNumber} from 'utils/fomatPhoneNumber';

/**
 * 초기 필터 데이터
 * 검색 필터의 초기값을 정의합니다.
 * @property {string} keyword - 검색 키워드
 * @property {string} affiliation - 소속
 * @property {string} physicalCondition - 신체 조건
 */
export const initialFilterData: {[key: string]: string} = {
  keyword: '',
  affiliation: '',
  physicalCondition: '',
  // state: '', // TODO! 추후 상태 업데이트하면 세팅
};

/**
 * 옵션 타입
 * @property {string} text - 표시 텍스트
 * @property {any} value - 옵션 값
 */
type IOption = {text: string; value: any};

/**
 * 사용자 관리 상세 페이지 컴포넌트
 * 특정 사용자의 권한 목록을 조회하는 페이지입니다.
 * @returns {JSX.Element} 사용자 관리 상세 페이지 컴포넌트
 */
const UserManagementUserDetail = () => {
  const {state} = useLocation();
  const device = getDevice();

  const userInfo = useRecoilValue(userInfoState);

  const [isLoading, setIsLoading] = useState(false);
  const [errorModal, setErrorModal] = useState({});

  const [permissionList, setPermissionList] = useState<any | null>();

  const [currentPage, setCurrentPage] = useState(1);

  // 모달
  const [modal, setModal] = useState('');

  /**
   * 사용자 권한 목록을 조회합니다.
   * 사용자 UUID를 기반으로 권한 목록을 가져옵니다.
   * @returns {Promise<void>}
   */
  const getPermissionList = async () => {
    const result = await searchUserPermission({
      setIsLoading,
      userInfo,
      setErrorModal,
      userUUID: state.uuid,
    });
    if (result) setPermissionList(result);
  };
  useEffect(() => {
    if (userInfo && state?.uuid) getPermissionList();
  }, [state, currentPage]);

  const modalData: {[key: string]: any} = {
    servicePreparing: {
      title: 'servicePreparing',
      firstButtonText: 'confirm',
      firstButtonClick: () => {
        setModal('');
      },
    },
  };

  const [changeStateModalInfo, setChangeStateModalInfo] = useState(null);
  const [resignationModalInfo, setResignationModalInfo] = useState<{
    userUUID: string;
    contractorUUID: string;
    affiliation: string;
    role: string;
  } | null>(null);
  const [changeNameModalInfo, setChangeNameModalInfo] = useState<any>(null);
  const [ownerTransferModalInfo, setOwnerTransferModalInfo] = useState<{
    userUUID: string;
    userName: string;
    targetType: 'CONTRACTOR' | 'PARTNER_COMPANY' | 'TEAM' | 'TONG_PLACE';
    targetUUID: string;
    targetName: string;
  } | null>(null);

  const getTargetType = (permissionType: string): 'CONTRACTOR' | 'PARTNER_COMPANY' | 'TEAM' | 'TONG_PLACE' => {
    const map: Record<string, 'CONTRACTOR' | 'PARTNER_COMPANY' | 'TEAM' | 'TONG_PLACE'> = {
      'ContractorPermission': 'CONTRACTOR',
      'PartnerCompanyPermission': 'PARTNER_COMPANY',
      'TongPlacePermission': 'TONG_PLACE',
      'TeamPermission': 'TEAM',
    };
    return map[permissionType] || 'CONTRACTOR';
  };

  return (
    <ScreenLayout isLoading={isLoading} errorModal={errorModal}>
      {/* <ServicePreparingInnerModal /> */}
      <Container>
        <ContainerHeader
          mainTitle="userManagement"
          subTitle="userManagementUser"
        />

        <Inner $device={device} style={{paddingTop: minmax(20)}}>
          <Header>
            <DefaultText
              text="roleList"
              textStyle={{
                fontSize: minmax(30),
                fontWeight: 'bold',
                borderBottom: `3px solid ${colors.primary}`,
                width: 'fit-content',
                marginBottom: minmax(20),
              }}
            />
          </Header>

          <div style={{width: '100%', marginBottom: minmax(30)}}>
            <div
              style={{
                borderRadius: minmax(16),
                display: 'flex',
                flexDirection: 'column',
                border: `1px solid ${colors.borderGray}`,
                padding: minmax(20),
                paddingLeft: minmax(30),
                paddingRight: minmax(30),
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: colors.lighterGray,
              }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}>
                <div style={{display: 'flex', alignItems: 'center', gap: minmax(8)}}>
                  <div style={{flex: 1}}>
                    <LabelInput
                      type={'text'}
                      labelText={'name'}
                      value={state?.info?.name || '-'}
                      readOnly
                      disabled
                      inputStyle={{
                        border: 0,
                        backgroundColor: colors.lighterGray,
                      }}
                    />
                  </div>
                  <DefaultButton
                    text="이름 변경"
                    actived={1}
                    buttonStyle={{
                      backgroundColor: colors.primary2,
                      padding: `${minmax(8)} ${minmax(16)}`,
                      height: minmax(40),
                      whiteSpace: 'nowrap',
                      marginTop: minmax(22),
                    }}
                    textStyle={{fontSize: minmax(16)}}
                    onClick={() => setChangeNameModalInfo({
                      uuid: state?.uuid,
                      userUUID: state?.uuid,
                      name: state?.info?.name || '',
                    })}
                  />
                </div>
                <LabelInput
                  type={'text'}
                  labelText={'phone'}
                  value={formatPhoneNumber({phone: state?.info?.phone || ''}) || '-'}
                  readOnly
                  disabled
                  inputStyle={{
                    border: 0,
                    backgroundColor: colors.lighterGray,
                  }}
                />
              </div>
            </div>
          </div>

          <ResultArea style={{marginBottom: minmax(120)}}>
            <ResultHeader>
              <DefaultText
                text=""
                textStyle={{...ResultHeaderTextStyle, flex: 1}}
              />
              <DefaultText
                text="affiliatedCompany"
                textStyle={{...ResultHeaderTextStyle, flex: 1}}
              />
              <DefaultText
                text="permission"
                textStyle={{...ResultHeaderTextStyle, flex: 1}}
              />
              <DefaultText
                text="approvalState"
                textStyle={{...ResultHeaderTextStyle, flex: 1}}
              />
              <DefaultText
                text="tongPlace"
                textStyle={{...ResultHeaderTextStyle, flex: 1}}
              />
              <DefaultText
                text="관리"
                textStyle={{...ResultHeaderTextStyle, flex: 0.7}}
              />
            </ResultHeader>

            <ResultList style={{maxHeight: minmax(500), overflowY: 'auto'}}>
              {permissionList ? (
                permissionList.length ? (
                  permissionList.map((item: any, index: any) => {
                    return (
                      <ResultItem
                        key={index}
                        onClick={() => {
                          // Item click handler
                        }}
                        style={{cursor: 'pointer'}}>
                       
                        <DefaultText
                          text={item?.permissionProfileType || '-'}
                          textStyle={{...ResultItemStyle, flex: 1}}
                        />
                        <DefaultText
                          text={item?.affiliation || '-'}
                          textStyle={{...ResultItemStyle, flex: 1}}
                        />
                        <RoleWrap>
                          <DefaultText
                            text={item?.permissionProfileType}
                            textStyle={ResultItemStyle}
                          />
                          <DefaultText
                            text={item?.role}
                            textStyle={ResultItemStyle}
                          />
                        </RoleWrap>

                        <DefaultText
                          text={item?.deletedAt
                            ? permissionStateStatus['RESIGNED']?.text
                            : permissionStateStatus[item?.state]?.text || '-'}
                          textStyle={{
                            ...ResultItemStyle,
                            flex: 1,
                            color: item?.deletedAt
                              ? permissionStateStatus['RESIGNED']?.color
                              : permissionStateStatus[item?.state]?.color ||
                                colors.black,
                            cursor: item?.deletedAt ? 'default' : 'pointer',
                          }}
                          onClick={() => {
                            if (!item?.deletedAt) setChangeStateModalInfo(item);
                          }}
                        />

                        <DefaultText
                          text={item.tongPlaceName || 'entireSite'}
                          textStyle={{...ResultItemStyle, flex: 1}}
                        />

                        <div style={{flex: 0.7, display: 'flex', gap: minmax(4), justifyContent: 'center'}}>
                          {item?.role === 'OWNER' && item?.state === 'ALLOWED' && !item?.deletedAt && (
                            <DefaultButton
                              text="OWNER 이양"
                              buttonStyle={{
                                backgroundColor: colors.primary,
                                padding: `${minmax(6)} ${minmax(8)}`,
                                borderRadius: minmax(4),
                              }}
                              textStyle={{
                                color: colors.white,
                                fontSize: minmax(12),
                              }}
                              onClick={() => {
                                setOwnerTransferModalInfo({
                                  userUUID: state.uuid,
                                  userName: state?.info?.name || '',
                                  targetType: getTargetType(item.permissionType),
                                  targetUUID: item.permissionResourceUUID,
                                  targetName: item.entityName || item.affiliation || '-',
                                });
                              }}
                            />
                          )}
                          {item?.permissionProfileType === 'CONTRACTOR' &&
                           item?.state === 'ALLOWED' && !item?.deletedAt && (
                            <DefaultButton
                              text="퇴사"
                              buttonStyle={{
                                backgroundColor: colors.vividRed,
                                padding: `${minmax(6)} ${minmax(12)}`,
                                borderRadius: minmax(4),
                              }}
                              textStyle={{
                                color: colors.white,
                                fontSize: minmax(14),
                              }}
                              onClick={() => {
                                setResignationModalInfo({
                                  userUUID: state.uuid,
                                  contractorUUID: item.permissionResourceUUID,
                                  affiliation: item.affiliation,
                                  role: item.role,
                                });
                              }}
                            />
                          )}
                          {item?.deletedAt && item?.permissionType === 'TongPlacePermission' && (
                            <DefaultButton
                              text="복원"
                              buttonStyle={{
                                backgroundColor: colors.primary,
                                padding: `${minmax(6)} ${minmax(12)}`,
                                borderRadius: minmax(4),
                              }}
                              textStyle={{
                                color: colors.white,
                                fontSize: minmax(14),
                              }}
                              onClick={async () => {
                                if (!window.confirm(`현장 권한을 복원하시겠습니까?\n유저: ${state?.info?.name || state.uuid}\n현장: ${item.tongPlaceName || item.permissionResourceUUID}`)) return;
                                const success = await restoreTongPlacePermission({
                                  userInfo,
                                  setIsLoading,
                                  setErrorModal,
                                  userUUID: state.uuid,
                                  tongPlaceUUID: item.permissionResourceUUID,
                                });
                                if (success) {
                                  await getPermissionList();
                                }
                              }}
                            />
                          )}
                        </div>
                      </ResultItem>
                    );
                  })
                ) : (
                  <DefaultText
                    text="조회된 권한이 없습니다."
                    textStyle={{
                      color: colors.darkGray,
                      textAlign: 'center',
                      margin: `${minmax(12)} 0`,
                    }}
                  />
                )
              ) : null}
            </ResultList>
          </ResultArea>
        </Inner>
      </Container>
      <Footer isMain />

      {changeStateModalInfo ? (
        <ChangeStateModal
          selectItem={changeStateModalInfo}
          closeModal={() => setChangeStateModalInfo(null)}
          setIsLoading={setIsLoading}
          setErrorModal={setErrorModal}
        />
      ) : (
        <></>
      )}

      {resignationModalInfo && (
        <ResignationModal
          selectItem={resignationModalInfo}
          userName={state?.info?.name || ''}
          closeModal={() => setResignationModalInfo(null)}
          onSuccess={() => {
            setResignationModalInfo(null);
            getPermissionList();
          }}
          setIsLoading={setIsLoading}
          setErrorModal={setErrorModal}
        />
      )}

      {ownerTransferModalInfo && (
        <OwnerTransferModal
          isOpen={true}
          onClose={() => setOwnerTransferModalInfo(null)}
          onComplete={() => {
            setOwnerTransferModalInfo(null);
            getPermissionList();
          }}
          userUUID={ownerTransferModalInfo.userUUID}
          userName={ownerTransferModalInfo.userName}
          targetType={ownerTransferModalInfo.targetType}
          targetUUID={ownerTransferModalInfo.targetUUID}
          targetName={ownerTransferModalInfo.targetName}
          setIsLoading={setIsLoading}
          setErrorModal={setErrorModal}
        />
      )}

      {changeNameModalInfo && (
        <ChangeUserNameModal
          selectItem={changeNameModalInfo}
          closeModal={() => setChangeNameModalInfo(null)}
          setIsLoading={setIsLoading}
          setErrorModal={setErrorModal}
        />
      )}
    </ScreenLayout>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  margin-bottom: ${minmax(120)};
`;

const Header = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  column-gap: ${minmax(27)};
`;

const Inner = styled.div<{$device?: string}>`
  width: 90%;
  padding-bottom: max(12px, min(1.5vw, 24px));
  padding-left: 2%;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${(props: any) =>
    props.$device !== 'pc' &&
    css`
      width: 100%;
    `}
`;

const ResultArea = styled.div`
  width: 100%;
  margin-bottom: ${minmax(39)};
  border-bottom: 1px solid ${colors.darkGray};
`;

const ResultHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: ${minmax(52)};
  background-color: #f7f7f7;
`;

const ResultList = styled.ul`
  // 스크롤바 삭제
`;

const ResultItem = styled.li`
  display: flex;
  flex-direction: row;
  align-items: center;
  min-height: ${minmax(52)};
  padding: ${minmax(12)} 0;
  border-top: 1px solid ${colors.lightGrey};
  &:last-child {
    border-bottom: none;
  }
`;

const RoleWrap = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
`;

const ResultHeaderTextStyle: React.CSSProperties = {
  textAlign: 'center',
  color: '#585858',
};

const ResultItemStyle: React.CSSProperties = {
  textAlign: 'center',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  wordBreak: 'break-all',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
};

export default UserManagementUserDetail;
